<script>
import CommentImagePopup from '@comp/document/comments/CommentImagePopup.vue'
import {
  reorderThreadSequences,
} from '@comp/document/threads/queries'
import { FontAwesomeLayers } from '@fortawesome/vue-fontawesome'
import { useIndexStore } from '@src/store/index.js'
import {
  THREAD_IMPORTANCE,
} from '@src/utils/consts'
import Sortable from 'sortablejs'

export default {
  name: 'ThreadsTableForPMEvaluation',
  components: {
    CommentImagePopup,
    FontAwesomeLayers,
  },
  inject: [
    'isThreadLockToggable',
    'displayThreadLockTitle',
    'getThreadLockIcon',
    'lockThreadToggle',
  ],
  props: {
    threads: {
      type: Array,
      required: true,
    },
    lockedMode: {
      type: Boolean,
      required: true,
    },
    showLockButton: {
      type: Boolean,
      required: true,
    },
    isOmanType: {
      type: Boolean,
      default: () => false,
    },
    dataProcessing: {
      type: Boolean,
      default: () => false,
    },
    documentVersionId: {
      type: Number,
      required: true,
    },
  },
  emits: [
    'add',
    'show',
  ],
  data () {
    return {
      showImagePopup: false,
      currentComment: null,
      sortable: null,
    }
  },
  computed: {
    store () {
      return useIndexStore()
    },
    canReorderThreads () {
      const docControllersId = this.store.project.docControllers.map(docController => docController.id)
      const managersId = this.store.project.managers.map(manager => manager.id)
      const currentUserId = this.store.user.id
      return ((this.store.user.isAdmin
        || docControllersId.includes(currentUserId)
        || managersId.includes(currentUserId))
      && !this.lockedMode)
    },
    headers () {
      const headers = [
        { title: this.$gettext('Métier'), key: 'trade', sortable: false, width: '8em' },
        { title: this.$gettext('Creation index'), key: 'createdAtVersion', sortable: false, width: '8em' },
        { title: this.$gettext('Importance'), key: 'importance', sortable: false, width: '6em' },
        { title: this.$gettext('Extract'), key: 'text', sortable: false, width: 'auto' },
        { title: this.$gettext('Actions'), key: 'actions', sortable: false, width: '140px' },
      ]
      return headers
    },
    MAX_CHARS () {
      return 30
    },
  },
  mounted () {
    this.initSortableLibrary()
  },
  methods: {
    initSortableLibrary () {
      const tbody = this.$refs.threadsTable?.$el.querySelector('tbody')
      if (!tbody) {
        return
      }
      this.sortable = Sortable.create(tbody, {
        animation: 150,
        handle: '.thread-drag-handle',
        onEnd: this.updateThreadsSequence,
      })
    },
    async updateThreadsSequence (event) {
      const dataPkElements = event.to.querySelectorAll('tr[data-pk]')
      const threadPks = []
      for (const dataPkElement of dataPkElements) {
        const stringId = dataPkElement.getAttribute('data-pk')
        const numberId = parseInt(stringId, 10)
        threadPks.push(numberId)
      }
      await this.$graphqlMutate(reorderThreadSequences, {
        documentVersionPk: this.documentVersionId,
        threadPks,
      }).then()
        .catch(error => {
          this.store.changeNotification({
            type: 'error',
            text: error,
            autoClose: false,
          })
        })
    },
    displayCreatedAtVersion (thread) {
      return thread.createdAtVersion.index
    },
    displayImportance (thread) {
      return this.isOmanType ? thread.importance : this.$gettext(THREAD_IMPORTANCE[thread.importance])
    },
    sortCommentsDescendingOrder (c1, c2) {
      if (c1.documentVersion.index > c2.documentVersion.index) {
        return -1
      } else if (c1.documentVersion.index < c2.documentVersion.index) {
        return 1
      } else {
        return 0
      }
    },
    getLastComment (thread) {
      let comments = []
      comments = thread.allComments.slice()
      comments.sort(this.sortCommentsDescendingOrder)
      return comments[0]
    },
    getLastCommentTextLines (thread) {
      const lastComment = this.getLastComment(thread)
      const sep = '</p>'
      const lines = lastComment.text.split(sep).filter(line => line).map(line => line + sep)
      // guaranteed to be a non-empty array
      return lines.length ? lines : ['']
    },
    displayLastCommentFirstLineFullText (thread) {
      const lines = this.getLastCommentTextLines(thread)
      const htmlText = new DOMParser().parseFromString(lines[0], 'text/html')
      const firstLine = htmlText.body.textContent
      if (lines.length >= 2) {
        return `${firstLine}[…]`
      } else {
        return firstLine
      }
    },
    displayLastCommentText (thread) {
      const lines = this.getLastCommentTextLines(thread)
      const htmlText = new DOMParser().parseFromString(lines[0], 'text/html')
      const firstLine = htmlText.body.textContent
      if (firstLine.length > this.MAX_CHARS) {
        return `${firstLine.substring(0, this.MAX_CHARS)}…`
      } else {
        return firstLine
      }
    },
    displayLastCommentImageLink (thread) {
      return this.getLastComment(thread).imageUrl
    },
    showImage (comment) {
      this.currentComment = comment
      this.showImagePopup = true
    },
    closeImage () {
      this.showImagePopup = false
      this.currentComment = null
    },
    add () {
      this.$emit('add')
    },
    displayShowThreadTitle (thread) {
      let title = this.$gettext('See the comment thread')
      title += ' (' + (thread.closed ? this.$gettext('Closed') : this.$gettext('Active')) + ')'
      return title
    },
    getThreadStatusSubIcon (thread) {
      return thread.closed ? 'fas fa-check' : 'fas fa-ellipsis-h'
    },
    canShow () {
      return !this.dataProcessing
    },
    show (thread) {
      this.$emit('show', thread)
    },
    isThreadLockEnabled (thread) {
      if (this.dataProcessing) {
        return false
      } else {
        return this.isThreadLockToggable(thread)
      }
    },
  },
}
</script>
<template>
  <v-data-table-virtual
    ref="threadsTable"
    :headers="headers"
    :items="threads"
    :row-props="({ item }) => ({ 'data-pk': item.id })"
    fixed-header
    class="threads-table"
  >
    <template
      #item.trade="{ item: thread }"
    >
      <div>
        <v-icon
          v-if="canReorderThreads"
          class="thread-drag-handle"
          size="small"
          icon="fas fa-up-down-left-right"
        />
        <span :title="thread.trade.name">{{ thread.trade.acronym }}</span>
      </div>
    </template>
    <template #item.createdAtVersion="{ item: thread }">
      {{ displayCreatedAtVersion(thread) }}
    </template>
    <template #item.importance="{ item: thread }">
      {{ displayImportance(thread) }}
    </template>
    <template #item.text="{ item: thread }">
      <!-- eslint-disable vue/no-v-html -->
      <div
        :title="displayLastCommentFirstLineFullText(thread)"
        v-html="displayLastCommentText(thread)"
      />
    </template>
    <template #item.imageUrl="{ item: thread }">
      <v-icon
        v-if="displayLastCommentImageLink(thread)"
        size="small"
        color="secondary"
        :title="$gettext('Show illustration')"
        icon="fas fa-paperclip"
        @click="showImage(getLastComment(thread))"
      />
    </template>
    <template #item.actions="{ item: thread }">
      <div class="row">
        <v-btn
          variant="text"
          size="small"
          icon
        >
          <v-icon>
            <font-awesome-layers
              class="fa-fw fa-layers"
              :title="displayShowThreadTitle(thread)"
              :disabled="!canShow()"
              @click="show(thread)"
            >
              <font-awesome-icon
                icon="fas fa-comments"
                color="secondary"
                transform="left-2"
              />
              <font-awesome-icon
                :icon="getThreadStatusSubIcon(thread)"
                transform="shrink-6 up-7 right-8"
                color="darkgreen"
              />
            </font-awesome-layers>
          </v-icon>
        </v-btn>
        <v-btn
          v-if="showLockButton"
          size="small"
          variant="text"
          color="secondary"
          :disabled="!isThreadLockEnabled(thread)"
          :title="displayThreadLockTitle(thread)"
          :icon="getThreadLockIcon(thread)"
          @click="lockThreadToggle(thread)"
        />
      </div>
    </template>
  </v-data-table-virtual>
  <CommentImagePopup
    v-if="showImagePopup && currentComment"
    :comment="currentComment"
    @close="closeImage"
  />
</template>
<style lang="scss" scoped>
.threads-table {
  overflow-y: auto;
  flex-grow: 1;
}
.fa-layers:hover {
  cursor: pointer;
}
.thread-drag-handle {
  cursor: grab;
  padding-right: 0.5rem;
}
</style>
